<script setup>
import {ref} from "vue";
import RegistrationConfirmation from "./registration/RegistrationConfirmation.vue";
import RegistrationAccount from "./registration/RegistrationAccount.vue";
import TryRegistrationSummary from "./registration/TryRegistrationSummary.vue";
import TryRegistrationCourses from "./registration/TryRegistrationCourses.vue";
import httpClient from "../lib/http/index.js";
import {useForm} from "laravel-precognition-vue";

const {privacyLink, conditionsLink, seasonStart} = defineProps(['privacyLink', 'conditionsLink', 'seasonStart']);

const http = httpClient();

const history = [];
const form = ref(null);
const step = ref('courses');
const error_message = ref(null);
const user = ref( {} );
const pricing = ref( [] );
const selected_courses = ref( {} );
const submit_loading = ref(false);
const is_auth = ref(false);
const checkboxes = ref({
    gdpr: false,
    gcs: false,
});
const subscribe_form = useForm('post', '/api/v1/subscriptions/try', {
    courses: {},
    gdpr: false,
    gcs: false,
});

let holidays = [],
    auth_signal = null;

function changeStep(value) {
    error_message.value = null;

    form.value.scrollIntoView({behavior: "smooth", block: "start", inline: "start"});

    if ( value === 'submit' ) {
        return submitForm();
    }

    if ( value === 'previous' ) {
        step.value = history.pop();
    } else {
        history.push( step.value );
        step.value = value;
    }
}

async function submitForm() {
    submit_loading.value = true;

    subscribe_form.courses = Object.values( selected_courses.value ).map( course => {
        return {
            id: course.id,
            course_type: course.course_type,
            scheduled_date: course.scheduled_date,
        };
    } );
    subscribe_form.gdpr = checkboxes.value.gdpr;
    subscribe_form.gcs = checkboxes.value.gcs;

    try {
        const response = await subscribe_form.submit();

        if ( response.data.status ) {
            form.value.submit();
        } else {
            error_message.value = response.data.message;
            submit_loading.value = false;
        }
    } catch (e) {
        console.error( e.message );
        submit_loading.value = false;
    }
}

async function authCheck() {
    try {
        if ( auth_signal ) {
            auth_signal.abort('Cancel request');
        }

        let response;

        auth_signal = http.signal();

        response = await http.get('/api/v1/auth/check', {}, {
            signal: auth_signal.signal
        });

        is_auth.value = response.body.status;

        if ( is_auth.value ) {
            response = await http.get('/api/v1/auth/me', {}, {
                signal: auth_signal.signal
            });

            user.value = response.body;
        }

        auth_signal = null;

        return is_auth.value;
    } catch (e) {
        console.error( e.message );
        auth_signal = null;
    }
}

async function pricingFetch() {
    try {
        const response = await http.get('/api/v1/subscriptions/try');

        pricing.value = response.body;
    } catch (e) {
        console.error( e.message );
    }
}

async function holidaysFetch() {
    try {
        const response = await http.get( '/api/v1/courses/holidays' );
        holidays = response.body.map( date => date.slice(0, 10) );
    } catch (e) {
        console.error( e.message );
    }
}

(async () => {
    await holidaysFetch();
    await pricingFetch();
    await authCheck();
})();
</script>

<template>
    <form method="post" action="/essayer" class="flex flex-col" ref="form">
        <try-registration-courses :courses="selected_courses" :pricing="pricing" :holidays="holidays" :season-start="seasonStart" @change="value => selected_courses = value" @step="changeStep" v-if="pricing.length > 0 && step === 'courses'"></try-registration-courses>
        <registration-account :value="is_auth" :user="user" @change="value => {is_auth = value; authCheck();}" @step="changeStep" v-if="step === 'account'">
            <try-registration-summary :courses="selected_courses" :pricing="pricing" :holidays="holidays"></try-registration-summary>
        </registration-account>
        <registration-confirmation :value="checkboxes" :loading="submit_loading" :privacy-link="privacyLink" :conditions-link="conditionsLink" @change="value => checkboxes = value" @step="changeStep" v-if="step === 'confirmation'">
            <try-registration-summary :courses="selected_courses" :pricing="pricing" :holidays="holidays"></try-registration-summary>
        </registration-confirmation>
        <transition>
            <div class="w-full lg:w-2/3 mt-10 mx-auto text-red-500 text-center" v-if="error_message" v-html="error_message"></div>
        </transition>
    </form>
</template>